import { combineReducers, configureStore } from '@reduxjs/toolkit';
import alarmVolumeReducer from 'features/Alarms/slices/alarmsSlice';
import deviceReducer from 'features/Integrations/state/slices/deviceSlice';
import integrationReducer from 'features/Integrations/state/slices/integrationSlice';
import alertCreatorReducer from 'features/Weather/features/WeatherAlerts/features/AlertCreator/state/slices/alertCreatorSlice';
import alertListReducer from 'features/Weather/features/WeatherAlerts/features/AlertList/state/slices/alertListSlice';
import { AlarmsApi } from 'services/Alarms/Alarms.api';
import { alertsApi } from 'services/alerts/alerts.api';
import { auditLogsV2Api } from 'services/auditLogs_v2/auditLogs_v2.api';
import { devicesApi } from 'services/devices/devices.api';
import { groupsApi } from 'services/devices/groups/groups.api';
import { dutiesApi } from 'services/duties/duties.api';
import { buoyDevicesApi } from 'services/geography/buoys/buoy.api';
import { geographyApi } from 'services/geography/geography.api';
import { usgsGeoApi } from 'services/geography/usgs/usgs.api';
import { globalActionsApi } from 'services/globalActions/globalActions.api';
import { femaApi } from 'services/ipaws/ipaws.api';
import { integrationsApi } from 'services/integrations/integrations.api';
import { organizationApi } from 'services/organization/organization.api';
import { rolesApi } from 'services/roles/roles.api';
import { placesApi } from 'services/user/places/places.api';
import { widgetsApi } from 'services/user/widgets/widgets.api';
import { airportsApi } from 'services/weather/airports/airports.api';
import { fireDangerApi } from 'services/weather/fireDanger/fireDanger.api';
import { marineApi } from 'services/weather/marine/marine.api';
import { skiApi } from 'services/weather/ski/ski.api';
import { weatherApi } from 'services/weather/weather.api';
import userReducer from 'store/slices/User/userSlice';
import displayOptionsReducer from './slices/DisplayOptions/displayOptionsSlice';
import scheduleSelectionCacheReducer from './slices/Responder/scheduleSelectionCacheSlice';
import widgetReducer from './slices/Widget/widgetSlice';
import { cleanRTKQuerySlice } from './utils/store.utils';

// Import and add new API slices here to add their middlewares
const apiSlices = [
  devicesApi,
  usgsGeoApi,
  buoyDevicesApi,
  groupsApi,
  integrationsApi,
  AlarmsApi,
  alertsApi,
  rolesApi,
  dutiesApi,
  organizationApi,
  widgetsApi,
  geographyApi,
  airportsApi,
  weatherApi,
  skiApi,
  marineApi,
  fireDangerApi,
  placesApi,
  globalActionsApi,
  auditLogsV2Api,
  femaApi,
];

const apiMiddlewares = apiSlices.map((api) => api.middleware);

// Allows us to add a root reducer that can manipulate the state before passing it to the reducers
const combinedReducer = combineReducers({
  integration: integrationReducer,
  device: deviceReducer,
  alertList: alertListReducer,
  alertCreator: alertCreatorReducer,
  widget: widgetReducer,
  user: userReducer,
  displayOptions: displayOptionsReducer,
  scheduleSelectionCache: scheduleSelectionCacheReducer,
  alarmVolume: alarmVolumeReducer,
  [devicesApi.reducerPath]: devicesApi.reducer,
  [usgsGeoApi.reducerPath]: usgsGeoApi.reducer,
  [buoyDevicesApi.reducerPath]: buoyDevicesApi.reducer,
  [groupsApi.reducerPath]: groupsApi.reducer,
  [integrationsApi.reducerPath]: integrationsApi.reducer,
  [AlarmsApi.reducerPath]: AlarmsApi.reducer,
  [alertsApi.reducerPath]: alertsApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [dutiesApi.reducerPath]: dutiesApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [widgetsApi.reducerPath]: widgetsApi.reducer,
  [geographyApi.reducerPath]: geographyApi.reducer,
  [airportsApi.reducerPath]: airportsApi.reducer,
  [weatherApi.reducerPath]: weatherApi.reducer,
  [skiApi.reducerPath]: skiApi.reducer,
  [marineApi.reducerPath]: marineApi.reducer,
  [fireDangerApi.reducerPath]: fireDangerApi.reducer,
  [placesApi.reducerPath]: placesApi.reducer,
  [globalActionsApi.reducerPath]: globalActionsApi.reducer,
  [auditLogsV2Api.reducerPath]: auditLogsV2Api.reducer,
  [femaApi.reducerPath]: femaApi.reducer,
});

const rootReducer = (state, action) => {
  if (action.type === 'store/reset') {
    state = undefined;
  }

  return combinedReducer(state, action);
};

export const resetStore = () => (dispatch) => {
  dispatch({ type: 'store/reset' });

  apiSlices.forEach((api) => {
    dispatch(api.util.resetApiState());
  });
};

export const store = configureStore({
  // Add reducers here when creating new slices
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(apiMiddlewares),
  devTools: {
    maxAge: 10,
    stateSanitizer: (state) => {
      const cleanedState = { ...state };

      apiSlices.forEach((api) => {
        const apiPath = api.reducerPath;
        cleanedState[apiPath] = cleanRTKQuerySlice(cleanedState[apiPath]);
      });

      return cleanedState;
    },
    actionsDenylist: [],
  },
});
